import { VTU_PENDING_PAYMENT_KEY } from "./constants";

export const formatItemLabel = (item) => {
  if (!item) return "Bill";
  return item.charAt(0).toUpperCase() + item.slice(1);
};

export const getVtuPaymentErrorMessage = (error) => {
  const backendError = error?.response?.data?.error;
  const fallbackMessage =
    error?.response?.data?.data ||
    error?.message ||
    "Payment verification failed";

  if (backendError?.code === "VTU_PROVIDER_FAILED") {
    if (backendError?.refundStatus === "succeeded") {
      return "Payment succeeded but provider failed. Refund has been initiated.";
    }

    return "Payment succeeded but provider failed. Our team is reviewing your refund.";
  }

  return fallbackMessage;
};

export const extractVariations = (responseData) => {
  const content = responseData?.content || responseData?.data?.content;
  const variations = content?.variations || responseData?.variations;
  return Array.isArray(variations) ? variations : [];
};

export const getVariationCode = (variation) =>
  variation?.variation_code ||
  variation?.variationCode ||
  variation?.code ||
  "";

export const getVariationName = (variation) =>
  variation?.name || variation?.variation || variation?.description || "Plan";

export const parseAmount = (amount) =>
  Number(String(amount ?? "").replace(/,/g, "")) || 0;

export const formatCurrency = (value) =>
  `₦${(Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const getVariationAmount = (variation) =>
  parseAmount(
    variation?.variation_amount ?? variation?.amount ?? variation?.price ?? 0,
  );

export const getCallbackUrl = (item) => {
  const url = new URL(window.location.href);
  url.searchParams.set("vtuPayReturn", "1");
  url.searchParams.set("vtuItem", item);
  return url.toString();
};

export const clearReturnParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("vtuPayReturn");
  url.searchParams.delete("vtuItem");
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
};

export const getStoredPendingPayment = () => {
  try {
    const raw = localStorage.getItem(VTU_PENDING_PAYMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    if (!parsed?.reference || !parsed?.transactionId) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

export const setStoredPendingPayment = (paymentData) => {
  try {
    localStorage.setItem(VTU_PENDING_PAYMENT_KEY, JSON.stringify(paymentData));
  } catch (error) {
    return null;
  }

  return null;
};

export const clearStoredPendingPayment = () => {
  try {
    localStorage.removeItem(VTU_PENDING_PAYMENT_KEY);
  } catch (error) {
    return null;
  }

  return null;
};

const unwrapInsuranceContent = (responseData) => {
  const content =
    responseData?.content ??
    responseData?.data?.content ??
    responseData?.data ??
    responseData;
  // Some endpoints (e.g. vehicle models) nest the array under content.original
  if (Array.isArray(content)) return content;
  if (Array.isArray(content?.original)) return content.original;
  return [];
};

export const extractInsuranceColors = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({ code: item.ColourCode, name: item.ColourName }))
    .filter((item) => item.code != null);

export const extractInsuranceBrands = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({ code: item.VehicleMakeCode, name: item.VehicleMakeName }))
    .filter((item) => item.code != null);

export const extractInsuranceStates = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({ code: item.StateCode, name: item.StateName }))
    .filter((item) => item.code != null);

export const extractInsuranceCapacities = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({ code: item.CapacityCode, name: item.CapacityName }))
    .filter((item) => item.code != null);

export const extractInsuranceModels = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({
      code: item.VehicleModelCode,
      name: item.VehicleModelName,
    }))
    .filter((item) => item.code != null);

export const extractInsuranceLGAs = (responseData) =>
  unwrapInsuranceContent(responseData)
    .map((item) => ({ code: item.LGACode, name: item.LGAName }))
    .filter((item) => item.code != null);
